// Toro Rewards on the storefront: redeem points for a single-use reward code (through the app proxy) that is added to the cart,
// and the "points redeemed" popup also used by the My Rewards page.
(() => {
  if (window.ToroRewards) return;
  const proxy = (document.documentElement.dataset.rewardsProxy || '/apps/rewards').replace(/\/$/, '');
  const money = n => '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  async function redeem(points) {
    const id = window.crypto && crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random();
    const r = await fetch(`${proxy}/redeem`, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ points, requestId: id }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || 'Could not use your points right now.');
    if (j.code) await applyCode(j.code);
    return j;
  }

  // Add a reward code to the cart alongside any promo code already there.
  async function applyCode(code) {
    const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
    const codes = (cart.discount_codes || []).map(d => d.code).filter(Boolean);
    if (!codes.some(c => c.toUpperCase() === code.toUpperCase())) codes.push(code);
    await fetch('/cart/update.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ discount: codes.join(',') }) });
  }

  // ---- Popup ----
  let modal = null, lastFocus = null;
  function close() {
    if (!modal) return;
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('toro-rw-lock');
    setTimeout(() => { modal.hidden = true; }, 200);
    if (lastFocus) lastFocus.focus();
  }
  function showRedeemed(dollars, { context = 'page' } = {}) {
    if (!modal) {
      modal = document.createElement('div');
      modal.className = 'toro-rw-modal';
      modal.hidden = true;
      modal.innerHTML = `
        <div class="toro-rw-modal__scrim" data-rw-close></div>
        <div class="toro-rw-modal__card" role="dialog" aria-modal="true" aria-labelledby="ToroRwTitle">
          <button type="button" class="toro-rw-modal__x" aria-label="Close" data-rw-close>&times;</button>
          <div class="toro-rw-modal__badge" aria-hidden="true"><svg viewBox="0 0 24 24" width="30" height="30"><path d="M5 12.5l4.2 4.2L19 7" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg></div>
          <p class="toro-rw-modal__eyebrow">Toro Rewards</p>
          <h2 id="ToroRwTitle" class="toro-rw-modal__title">Your points have been redeemed!</h2>
          <p class="toro-rw-modal__amount"><strong data-rw-amount></strong> off has been added to your cart.</p>
          <ol class="toro-rw-modal__steps">
            <li><span>&#10003;</span>Your reward is already applied. You'll see it in your cart total.</li>
            <li><span>&#10003;</span>Just check out as usual. Nothing else to do.</li>
          </ol>
          <div class="toro-rw-modal__actions" data-rw-actions></div>
        </div>`;
      document.body.appendChild(modal);
      modal.addEventListener('click', e => { if (e.target.closest('[data-rw-close]')) close(); });
      document.addEventListener('keydown', e => { if (e.key === 'Escape' && modal && !modal.hidden) close(); });
    }
    modal.querySelector('[data-rw-amount]').textContent = money(dollars);
    modal.querySelector('[data-rw-actions]').innerHTML = context === 'cart'
      ? '<a class="toro-rw-modal__btn toro-rw-modal__btn--gold" href="/checkout">Go to checkout</a><button type="button" class="toro-rw-modal__btn" data-rw-close>Keep shopping</button>'
      : '<a class="toro-rw-modal__btn toro-rw-modal__btn--gold" href="/cart">View my cart</a><a class="toro-rw-modal__btn" href="/collections/all">Keep shopping</a>';
    lastFocus = document.activeElement;
    modal.hidden = false;
    document.documentElement.classList.add('toro-rw-lock');
    requestAnimationFrame(() => { modal.classList.add('is-open'); modal.querySelector('.toro-rw-modal__btn--gold').focus(); });
  }

  function pick(box, steps) {
    box.dataset.picked = String(steps);
    box.querySelectorAll('[data-rw-steps]').forEach(c => c.setAttribute('aria-checked', String(Number(c.dataset.rwSteps) === steps)));
    const btn = box.querySelector('[data-rw-apply]');
    if (btn) { btn.disabled = !steps; btn.textContent = `Use ${money(steps * Number(box.dataset.stepCents) / 100).replace('.00', '')} on this order`; }
  }

  // ---- Cart picker (re-initialised every time the cart re-renders) ----
  function initCart() {
    const savedRows = document.querySelectorAll('[data-rw-saved]');
    if (savedRows.length) fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json()).then(cart => {
      const inCart = (cart.discount_codes || []).map(d => String(d.code).toUpperCase());
      savedRows.forEach(row => { if (inCart.includes(row.dataset.rwSaved.toUpperCase())) row.remove(); });
    }).catch(() => {});
    document.querySelectorAll('[data-rw-num]').forEach(el => { const n = Number(el.textContent); if (!isNaN(n)) el.textContent = n.toLocaleString('en-US'); });
    document.querySelectorAll('[data-rw-cart]').forEach(box => {
      const chips = box.querySelector('[data-rw-chips]');
      if (!chips || chips.dataset.ready) return;
      chips.dataset.ready = '1';
      const step = Number(box.dataset.step) || 500, stepCents = Number(box.dataset.stepCents) || 500;
      const max = Number(box.dataset.maxSteps) || 0;
      let opts = [];
      for (let i = 1; i <= max; i++) opts.push(i);
      if (opts.length > 4) opts = [1, 2, Math.ceil(max / 2), max].filter((v, i, a) => a.indexOf(v) === i && v > 0);
      // One option: no picker, the button is ready. Several: small pills, largest pre-selected.
      chips.hidden = opts.length < 2;
      chips.innerHTML = opts.length < 2 ? '' : opts.map(n => `<button type="button" class="toro-cart__rw-chip" role="radio" aria-checked="false" data-rw-steps="${n}">${money(n * stepCents / 100).replace('.00', '')}</button>`).join('');
      pick(box, max);
    });
  }

  document.addEventListener('click', async e => {
    const chip = e.target.closest('[data-rw-steps]');
    if (chip) { pick(chip.closest('[data-rw-cart]'), Number(chip.dataset.rwSteps)); return; }
    const saved = e.target.closest('[data-rw-code]');
    if (saved) {
      saved.disabled = true; saved.textContent = 'Applying...';
      try { await applyCode(saved.dataset.rwCode); } catch (err) {}
      if (window.ToroCart && window.ToroCart.refresh && document.querySelector('[data-rw-cart]')) { await window.ToroCart.refresh(); initCart(); }
      else window.location.href = '/cart';
      return;
    }
    const apply = e.target.closest('[data-rw-apply]');
    if (!apply) return;
    const box = apply.closest('[data-rw-cart]');
    const steps = Number(box.dataset.picked);
    if (!steps) return;
    const msg = box.querySelector('[data-rw-msg]');
    apply.disabled = true; apply.textContent = 'Applying...'; msg.textContent = ''; msg.className = 'toro-cart__rw-msg';
    try {
      const j = await redeem(steps * Number(box.dataset.step));
      showRedeemed(j.credited, { context: 'cart' });
      if (window.ToroCart && window.ToroCart.refresh) await window.ToroCart.refresh();
      initCart();
    } catch (err) {
      msg.className = 'toro-cart__rw-msg is-err';
      msg.textContent = err.message;
      apply.disabled = false; apply.textContent = 'Try again';
    }
  });

  document.addEventListener('toro:cart-rendered', initCart);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initCart); else initCart();

  window.ToroRewards = { redeem, applyCode, showRedeemed };
})();
